import { useState } from 'react';
import { Check, Globe, Lock, Bell } from 'lucide-react';
import { useSetOptional, useSaveTheme, useSetStatus } from './hooks.ts';
import { apiErrorCode } from './api.ts';
import type { OptionalBundle, SiteInfo } from './api.ts';

const PUBLIC_BASE = 'site.wapplify.app';

function OptionalToggle({ id, titleEn, titleDv, enabled }: { id: string; titleEn: string; titleDv: string; enabled: boolean }): JSX.Element {
  const setOptional = useSetOptional();
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{titleEn}</p>
        <p className="thaana text-sm text-muted-foreground" dir="rtl">{titleDv}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`Toggle ${titleEn}`}
        disabled={setOptional.isPending}
        onClick={() => setOptional.mutate({ sectionId: id, enabled: !enabled })}
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${enabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

function CustomDomainForm({ site }: { site: SiteInfo }): JSX.Element {
  const save = useSaveTheme();
  const [domain, setDomain] = useState(site.customDomain ?? '');
  const [saved, setSaved] = useState(false);
  const dirty = domain.trim() !== (site.customDomain ?? '');
  const errCode = apiErrorCode(save.error);

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    setSaved(false);
    save.mutate({ customDomain: domain.trim() }, { onSuccess: () => setSaved(true) });
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <label htmlFor="customDomain" className="status-label flex items-center gap-1.5"><Globe size={12} /> Custom domain</label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id="customDomain"
          type="text"
          dir="ltr"
          placeholder="council.gov.mv"
          className="input font-mono text-sm max-w-xs"
          value={domain}
          onChange={(e) => { setSaved(false); setDomain(e.target.value); }}
        />
        <button type="submit" className="btn-secondary btn-sm" disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved && !save.isPending && (
          <span className="inline-flex items-center gap-1 text-sm text-[hsl(var(--success))]" role="status">
            <Check size={14} /> Saved
          </span>
        )}
      </div>
      {save.isError && (
        <p role="alert" className="text-xs text-destructive">
          {errCode === 'domain_taken'
            ? 'That domain is already used by another council.'
            : `Couldn't save the domain${errCode ? ` (${errCode})` : ''}. Please try again.`}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        DNS &amp; TLS for a custom domain are configured separately — this only records the domain for now.
      </p>
    </form>
  );
}

// Where service-application-form submissions are emailed. Stored in theme JSON;
// falls back to the org's contact email server-side when blank.
function NotifyEmailForm({ site }: { site: SiteInfo }): JSX.Element {
  const save = useSaveTheme();
  const [email, setEmail] = useState(site.theme.notifyEmail ?? '');
  const [saved, setSaved] = useState(false);
  const dirty = email.trim() !== (site.theme.notifyEmail ?? '');

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    setSaved(false);
    save.mutate({ theme: { ...site.theme, notifyEmail: email.trim() } }, { onSuccess: () => setSaved(true) });
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <label htmlFor="notifyEmail" className="status-label flex items-center gap-1.5"><Bell size={12} /> Notifications email</label>
      <div className="flex flex-wrap items-center gap-2">
        <input id="notifyEmail" type="email" dir="ltr" placeholder="office@council.gov.mv" className="input text-sm max-w-xs" value={email} onChange={(e) => { setSaved(false); setEmail(e.target.value); }} />
        <button type="submit" className="btn-secondary btn-sm" disabled={!dirty || save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        {saved && !save.isPending && <span className="inline-flex items-center gap-1 text-sm text-[hsl(var(--success))]" role="status"><Check size={14} /> Saved</span>}
      </div>
      <p className="text-xs text-muted-foreground">New service-form applications are emailed here. Leave blank to use your council's contact email.</p>
    </form>
  );
}

// Settings tab — subdomain (read-only), custom domain, optional-section toggles,
// and the publish status + Publish/Unpublish control.
export function SettingsPanel({ site, optional }: { site: SiteInfo; optional: OptionalBundle[] }): JSX.Element {
  const setStatus = useSetStatus();
  const setOptional = useSetOptional();
  const published = site.status === 'PUBLISHED';

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="font-serif text-2xl tracking-tight text-ink">Settings</h2>
        <p className="text-sm text-muted-foreground">Address, publishing and optional sections.</p>
      </header>

      {/* Address */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink">Web address</h3>
        <div className="card-paper p-4 space-y-4">
          <div className="space-y-1">
            <label className="status-label flex items-center gap-1.5"><Lock size={12} /> Subdomain</label>
            <p className="mono-meta text-sm text-foreground" dir="ltr">{site.subdomain}.{PUBLIC_BASE}</p>
            <p className="text-xs text-muted-foreground">Your permanent WapSite address. It can't be changed here.</p>
          </div>
          <div className="border-t border-border pt-4">
            <CustomDomainForm site={site} />
          </div>
          <div className="border-t border-border pt-4">
            <NotifyEmailForm site={site} />
          </div>
        </div>
      </section>

      {/* Publishing */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink">Publishing</h3>
        <div className="card-paper p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`pill ${published ? 'pill-success' : 'pill-pending'}`}>{published ? 'Published' : 'Draft'}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              {published
                ? 'Your site is live and visible to the public.'
                : 'Your site is a private draft — the public URL shows a "not found" page until you publish.'}
            </p>
          </div>
          <button
            type="button"
            className={published ? 'btn-secondary' : 'btn-primary'}
            disabled={setStatus.isPending}
            onClick={() => setStatus.mutate(published ? 'DRAFT' : 'PUBLISHED')}
          >
            {setStatus.isPending ? 'Saving…' : published ? 'Unpublish' : 'Publish site'}
          </button>
        </div>
        {setStatus.isError && (
          <p role="alert" className="text-sm text-destructive">
            Couldn't change the publish state{apiErrorCode(setStatus.error) ? ` (${apiErrorCode(setStatus.error)})` : ''}. Please try again.
          </p>
        )}
      </section>

      {/* Optional sections */}
      <section className="space-y-3">
        <h3 className="section-heading font-serif text-lg text-ink">Optional sections</h3>
        <div className="card-paper p-4 divide-y divide-border">
          {optional.map((o) => (
            <OptionalToggle key={o.id} id={o.id} titleEn={o.titleEn} titleDv={o.titleDv} enabled={o.enabled} />
          ))}
        </div>
        {setOptional.isError && (
          <p role="alert" className="text-sm text-destructive">
            Couldn't update that section{apiErrorCode(setOptional.error) ? ` (${apiErrorCode(setOptional.error)})` : ''}. Please try again.
          </p>
        )}
      </section>
    </div>
  );
}
